import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {getAssetUrl} from '@Utils/s3.util';
import {openErrorModal} from '@Modals/error-modal';
import {openMessageModal} from '@Modals/message-modal';
import {useAppSelector} from '@/hooks';
import styles from './inactive-user-password-reset-form.module.scss';
import type {InactiveUserPasswordResetFormProps, MailProviderButtonContent, ResendEmailError} from './inactive-user-password-reset-form.types';
import {RESEND_EMAIL_DELAY_SECONDS} from './inactive-user-password-reset-form.types';

/**
 * This screen takes the forgot-password confirmation's layout — the
 * 20px heading-to-body gap and 32px body-to-action gap, an h5 title and a full-width 40px action — because
 * it is the same "a reset email is on its way" moment. The in-editor modal renders the same form, centred.
 */
export function InactiveUserPasswordResetForm({isFormInModal = false}: InactiveUserPasswordResetFormProps): ReactElement {
  const [delayBeforeResend, setDelayBeforeResend] = useState(RESEND_EMAIL_DELAY_SECONDS);
  const [isActiveTimer, setIsActiveTimer] = useState(true);

  const sniperLink = useAppSelector((state) => {
    return state.loginPage.inactiveUserPasswordResetSniperLink;
  });

  useEffect(() => {
    if (!isActiveTimer) {
      return undefined;
    }

    const interval = setInterval(() => {
      setDelayBeforeResend((previousDelay) => {
        if (previousDelay <= 1) {
          clearInterval(interval);
          setIsActiveTimer(false);
          return 0;
        }
        return previousDelay - 1;
      });
    }, 1000);

    return (): void => {
      clearInterval(interval);
    };
  }, [isActiveTimer]);

  useEffect(() => {
    if (isActiveTimer) {
      setDelayBeforeResend(RESEND_EMAIL_DELAY_SECONDS);
    }
  }, [isActiveTimer]);

  const getMailProviderButtonContent = (link: string): MailProviderButtonContent => {
    if (link.includes('mail.google.com')) {
      return {logoUrl: getAssetUrl('gmail-mail-logo.svg'), label: window.i18next.t('pmwjs_open_gmail')};
    }
    if (link.includes('outlook.live.com')) {
      return {logoUrl: getAssetUrl('outlook-mail-logo.svg'), label: window.i18next.t('pmwjs_open_outlook')};
    }
    if (link.includes('mail.yahoo.com')) {
      return {logoUrl: getAssetUrl('yahoo-mail-logo.svg'), label: window.i18next.t('pmwjs_open_yahoo')};
    }
    if (link.includes('mail.proton.me')) {
      return {logoUrl: getAssetUrl('proton-mail-logo.svg'), label: window.i18next.t('pmwjs_open_proton_mail')};
    }
    if (link.includes('www.icloud.com')) {
      return {logoUrl: getAssetUrl('icloud-mail-logo.svg'), label: window.i18next.t('pmwjs_open_icloud_mail')};
    }
    if (link.includes('mail.aol.com')) {
      return {logoUrl: getAssetUrl('aol-mail-logo.svg'), label: window.i18next.t('pmwjs_open_aol')};
    }
    return {logoUrl: '', label: window.i18next.t('pmwjs_open_mail')};
  };

  const getMailProviderLogo = (logoUrl: string): ReactElement => {
    if (logoUrl === '') {
      return <Icon icon="icon-envelope" size={IconSize.SIZE_ICON_16} className={styles.mailButtonIcon} />;
    }
    return <img height={16} width={16} src={logoUrl} alt="" />;
  };

  const getMailButtonContent = (): ReactElement => {
    const {logoUrl, label} = getMailProviderButtonContent(sniperLink ?? '');

    return (
      <div className={styles.mailButtonContent}>
        {getMailProviderLogo(logoUrl)}
        <Text val={label} size={TextSize.MD} bold className={styles.mailButtonLabel} />
        <Icon icon="icon-external-link" size={IconSize.SIZE_ICON_16} className={styles.mailButtonIcon} />
      </div>
    );
  };

  const onMailProviderClick = (): void => {
    if (sniperLink) {
      window.open(sniperLink, '_blank');
    }
  };

  const resendInactivePasswordResetEmail = async (): Promise<void> => {
    try {
      await window.PMW.writeLocal('authenticate/resendInactiveUserPasswordResetEmail', {});
      openMessageModal({
        title: window.i18next.t('pmwjs_password_reset_email_sent'),
        text: window.i18next.t('pmwjs_inactive_user_password_reset_email_resent'),
      });
    } catch (e) {
      const error = e as ResendEmailError;
      openErrorModal({
        message: error.message ?? window.i18next.t('pmwjs_something_wrong_reload_page'),
        log: false,
      });
    }

    setIsActiveTimer(true);
  };

  const onResendEmailClick = (): void => {
    if (delayBeforeResend > 0) {
      return;
    }

    void resendInactivePasswordResetEmail();
  };

  const getCountdownMarkup = (): string => {
    return `<span class="${styles.resendCountdown}">(${delayBeforeResend.toString()}s)</span>`;
  };

  const getResendPrompt = (): ReactElement => {
    if (isActiveTimer) {
      return (
        <Text
          val={`${window.i18next.t('pmwjs_inactive_user_password_reset_email_not_sent_disabled')} ${getCountdownMarkup()}`}
          dangerouslySetInnerHTML
          size={TextSize.MD}
          className={`${styles.resendPrompt} ${styles.resendPromptWaiting}`}
        />
      );
    }

    return (
      <Text
        val={window.i18next.t('pmwjs_inactive_user_password_reset_email_not_sent_enabled')}
        dangerouslySetInnerHTML
        size={TextSize.MD}
        className={`${styles.resendPrompt} ${styles.resendPromptReady}`}
        onClick={onResendEmailClick}
      />
    );
  };

  const getLayoutClassName = (): string => {
    return isFormInModal ? `${styles.resetLayout} ${styles.resetLayoutInModal}` : styles.resetLayout;
  };

  return (
    <div className={getLayoutClassName()}>
      <div className={styles.resetHeading}>
        <Icon
          icon="icon-email-repo-normal"
          size={IconSize.SIZE_ICON_24}
          shape={IconShape.CIRCLE}
          className={styles.resetHeadingIcon}
          type={IconType.FLAT}
          flatIconType={{color: FlatIconColor.SUCCESS, type: FlatIconColorType.DEFAULT}}
        />
        <Text val={window.i18next.t('pmwjs_welcome_back')} className={styles.resetTitle} />
      </div>

      <div className={styles.resetContent}>
        <Text val={window.i18next.t('pmwjs_inactive_user_password_reset_description')} size={TextSize.MD} className={styles.resetDescription} />
        <div className={styles.resetActions}>
          <Button type={ButtonType.SECONDARY} size={ButtonSize.MEDIUM} customText={getMailButtonContent()} isFullWidth onClick={onMailProviderClick} />
          {getResendPrompt()}
        </div>
      </div>
    </div>
  );
}
